import {
  CustomQuantityOptionDto,
  QuantityOptionDto,
  QuantityStepDto,
} from '@/api-sdk';
import { BREAKPOINT_EXTRA_LARGE_PX, BREAKPOINT_SMALL_PX } from '@/settings';
import {
  baseStyles,
  formStyles,
  loadingStyles,
  quantityTableStyles,
} from '@/styles';
import { getNumberValueFromString } from '@/utils';
import { localized, t } from '@weavedev/lit-i18next';
import { LitElement, css, html, nothing } from 'lit';
import { live } from 'lit-html/directives/live.js';
import { customElement, property, state } from 'lit/decorators.js';
import debounce from 'lodash-es/debounce';

import '../../../configuration/hsp/fg-hsp-label';

@customElement('fg-quantity-table')
@localized()
export class FgQuantityTable extends LitElement {
  @property({ type: Array })
  options: QuantityStepDto['quantityOptions'] = [];

  @property({ type: Object })
  customQuantityOption?: CustomQuantityOptionDto;

  @property({ type: Boolean })
  loading = true;

  @state()
  customQuantity = { value: 0 };

  @state()
  prevCustomQuantity = { value: 0 };

  @state()
  firstInteracted = false;

  private MAX_QUANTITY_VALUE = 1000000;

  renderCustomQuantityOption() {
    const { quantity, totalPrice, unitPrice, volumeDiscountPercentage } =
      this.customQuantityOption || {};

    const quantityInputValue =
      this.customQuantity.value > 0 ? this.customQuantity.value.toString() : '';

    const customQuantityOptionQuantity = quantity?.toString() || '';

    return html`
      <tfoot>
        <tr
          class="${quantity ? 'selected' : ''} ${this.loading ? 'loading' : ''}"
        >
          <td class="custom-quantity-input-td amount">
            <input
              class="custom-quantity-input"
              .placeholder=${t('labels.customQuantity') || ''}
              .value=${live(
                this.firstInteracted
                  ? quantityInputValue
                  : customQuantityOptionQuantity,
              )}
              @input=${(event: KeyboardEvent) => {
                const eventTarget = event.target as HTMLInputElement;
                const value = parseFloat(eventTarget.value);
                if (value > this.MAX_QUANTITY_VALUE) {
                  eventTarget.value = this.prevCustomQuantity.value?.toString();
                  this.customQuantity = this.prevCustomQuantity;
                  return;
                }
                this.prevCustomQuantity = { value: isNaN(value) ? 0 : value };
                this.setCustomQuantity(event);
              }}
            />
          </td>
          <td class="custom-quantity-input-td discount-percentage">
            <span>
              ${volumeDiscountPercentage
                ? html`
                    ${volumeDiscountPercentage}%
                  `
                : html`
                    -
                  `}
            </span>
          </td>
          <td class="custom-quantity-input-td">
            <span>${unitPrice ?? '-'}</span>
          </td>
          <td class="custom-quantity-input-td">
            <span>${totalPrice ?? '-'}</span>
          </td>
        </tr>
      </tfoot>
    `;
  }

  render() {
    return html`
      <table class="quantities">
        <thead>
          <tr>
            <th class="amount">${t('labels.quantity')}</th>
            <th>${t('labels.discount')}</th>
            <th class="mobile-hidden">${t('labels.pricePerItem')}</th>
            <th class="mobile-show">${t('labels.pricePerItemMb')}</th>
            <th>${t('labels.total')}</th>
          </tr>
        </thead>

        <tbody>
          ${this.options &&
          this.options.map(option => {
            const {
              quantity,
              queryString,
              selected,
              totalPrice,
              unitPrice,
              volumeDiscountPercentage,
              hspAvailable,
            } = option;

            return html`
              <tr
                class="${selected ? 'selected' : ''} ${this.loading
                  ? 'loading'
                  : ''}"
                @click=${() => this.selectOption(queryString)}
              >
                <td class="amount">
                  <span class="amount-wrapper small">
                    <span>${quantity}</span>
                    ${hspAvailable
                      ? html`
                          <fg-hsp-label onlySkewedLabel></fg-hsp-label>
                        `
                      : nothing}
                  </span>
                </td>
                <td class="discount-percentage">
                  <span>
                    ${volumeDiscountPercentage
                      ? html`
                          ${volumeDiscountPercentage}%
                        `
                      : html`
                          -
                        `}
                  </span>
                </td>
                <td><span>${unitPrice}</span></td>
                <td><span>${totalPrice}</span></td>
              </tr>
            `;
          })}
        </tbody>

        ${this.renderCustomQuantityOption()}
      </table>
    `;
  }

  selectOption(queryString: QuantityOptionDto['queryString']) {
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: { option: { queryString } },
      }),
    );
  }

  setCustomQuantity(event: KeyboardEvent) {
    const eventTarget = event.target as HTMLInputElement;
    let quantity = parseFloat(getNumberValueFromString(eventTarget.value));

    this.firstInteracted = true;

    if (isNaN(quantity)) {
      quantity = 0;
    }

    this.customQuantity = {
      ...this.customQuantity,
      value: quantity,
    };

    this.debounceDispatchSetQuantity();
  }

  debounceDispatchSetQuantity = debounce(
    () =>
      this.dispatchEvent(
        new CustomEvent('quantity-set', {
          composed: true,
          detail: {
            quantity: this.customQuantity.value,
            queryString:
              this.customQuantityOption &&
              this.customQuantityOption.queryString + this.customQuantity.value,
          },
        }),
      ),
    300,
  );

  static styles = [
    baseStyles,
    formStyles,
    quantityTableStyles,
    loadingStyles,
    css`
      .quantities tbody tr {
        cursor: pointer;
      }

      .quantities tr.selected td {
        font-weight: 700;
        background: var(
          --fg-config-selected--light,
          var(--fg-fallback-config-selected--light)
        );
        border: 2px solid
          var(--fg-config-selected, var(--fg-fallback-config-selected));
        border-style: solid none;
      }

      .quantities tbody tr:first-child:not(.selected) td {
        border-top: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
      }

      .quantities tr.selected td:first-child {
        border-left-style: solid;
        border-top-left-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        border-bottom-left-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
      }

      .quantities tr.selected td:last-child {
        border-right-style: solid;
        border-top-right-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        border-bottom-right-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
      }

      .quantities tr.selected td.discount-percentage {
        color: var(--fg-message-success, var(--fg-fallback-message-success));
      }

      .custom-quantity-input {
        width: 75px;
      }

      .mobile-hidden {
        display: none;
      }
      .mobile-show {
        display: table-cell;
      }

      .amount-wrapper {
        align-items: center;
        display: flex;
        > span:nth-child(1) {
          display: inline-block;
          width: 60px;
        }
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        .custom-quantity-input {
          width: 111px;
        }

        .quantities-custom-input td {
          padding-top: 4px;
          padding-bottom: 4px;
        }

        .mobile-show {
          display: none;
        }

        .mobile-hidden {
          display: table-cell;
        }
      }

      .setup-price td {
        border-top: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
      }

      .setup-price td:first-child {
        text-align: left;
      }

      .setup-price td:last-child {
        font-weight: 700;
      }

      .quantities tfoot > tr > td.custom-quantity-input-td {
        padding-top: 4px;
        padding-bottom: 4px;
      }

      @media screen and (min-width: ${BREAKPOINT_EXTRA_LARGE_PX}px) {
        .custom-quantity-input {
          width: 135px;
        }
      }
    `,
  ];
}
